type BindableElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement | HTMLDivElement;

type Path<T, K extends keyof T = keyof T> =
    K extends string
    ? T[K] extends Record<string, any> // Check if it's an object
    ? K | `${K}.${Path<T[K]>}` // Allow direct key or nested key (dot notation)
    : K // If not an object, only allow the direct key
    : never;

interface BindingInfo<T> {
    key: Path<T>;
    element: BindableElement;
    handleEvent: EventListener;
}

export class TwoWayBindingHelper<T extends object> {
    private _obj: T;
    private readonly _bindings: Map<string, BindingInfo<T>[]>;

    constructor(obj: T) {
        this._obj = obj;
        this._bindings = new Map<string, BindingInfo<T>[]>();
    }

    public bindToInput<K extends Path<T>>(keyPath: K, elementId: string, parentElement?: HTMLElement): void {
        const element = (parentElement ? parentElement.querySelector(`#${elementId}`) : document.getElementById(elementId)) as BindableElement | null;
        if (!element)
            return;

        const handleEvent = this._createEventHandler(keyPath, element);
        element.addEventListener('input', handleEvent);
        element.addEventListener('change', handleEvent);

        if (!this._bindings.has(keyPath))
            this._bindings.set(keyPath, []);

        this._bindings.get(keyPath)!.push({ key: keyPath, element, handleEvent });

        // Define getter and setter for nested properties dynamically
        this._defineReactiveProperty(keyPath);

        // Initialize the element with the current value
        this._updateElement(element, this._getPropertyValue(this._obj, keyPath));
    }

    private _defineReactiveProperty<K extends Path<T>>(keyPath: K): void {
        const keys = keyPath.split('.');

        if (keys.length === 1) { // Case 1: Direct property
            let value = this._obj[keys[0] as keyof T];

            Object.defineProperty(this._obj, keys[0] as keyof T, {
                get: () => value,
                set: (newValue) => {
                    value = newValue;
                    this._updateElementsForKey(keyPath, newValue);
                },
                configurable: true,
                enumerable: true,
            });
        } else { // Case 2: Nested property
            const lastKey = keys.pop()!; // The last key is what we define a property on
            const parent = keys.reduce((o, k) => (o[k] = o[k] || {}), this._obj as any); // Navigate to the parent object

            let value = this._getPropertyValue(this._obj, keyPath); // Get initial value

            //this._setPropertyValue(this._obj, keyPath, value);

            Object.defineProperty(this._obj, keyPath, {
                get: () => value,
                set: (newValue) => {
                    this._setPropertyValue(this._obj, keyPath, newValue);
                    this._updateElementsForKey(keyPath, newValue);
                },
                configurable: true,
                enumerable: true,
            });
        }
    }

    private _createEventHandler<K extends Path<T>>(keyPath: K, element: BindableElement): EventListener {
        return () => {
            const newValue = this._getElementValue(element);
            if (this._getPropertyValue(this._obj, keyPath) !== newValue) {
                this._setPropertyValue(this._obj, keyPath, newValue);
                this._updateElementsForKey(keyPath, newValue);
            }
        };
    }

    private _updateElementsForKey<K extends Path<T>>(keyPath: K, newValue: any): void {
        const bindings = this._bindings.get(keyPath);
        if (bindings) {
            for (const binding of bindings) {
                this._updateElement(binding.element, newValue);
            }
        }
    }

    private _updateElement(element: BindableElement, value: any): void {
        if (element instanceof HTMLInputElement) {
            if (element.type === 'checkbox' || element.type === 'radio')
                element.checked = !!value;
            else if (element.type === "date" && value instanceof Date)
                element.valueAsDate = value
            else
                element.value = value != null ? String(value) : '';
        } else if ('value' in element)
            (element as HTMLInputElement).value = value != null ? String(value) : '';
    }

    private _getElementValue(element: BindableElement): any {
        if (element instanceof HTMLInputElement) {
            if (element.type === 'checkbox' || element.type === 'radio')
                return element.checked;
            else
                return element.value;
        } else if ('value' in element)
            return (element as HTMLInputElement).value;
        else
            return null;
    }

    private _getPropertyValue(obj: any, keyPath: string): any {
        return keyPath.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
    }

    private _setPropertyValue(obj: any, keyPath: string, value: any): void {
        const keys = keyPath.split('.');
        const lastKey = keys.pop()!;
        const target = keys.reduce((o, k) => (o[k] = o[k] || {}), obj);
        target[lastKey] = value;
    }

    public clearListeners(): void {
        for (const bindings of this._bindings.values()) {
            for (const binding of bindings) {
                binding.element.removeEventListener('input', binding.handleEvent);
                binding.element.removeEventListener('change', binding.handleEvent);
            }
        }
        this._bindings.clear();
    }
}